import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import { isEmpty } from 'lodash';
import React, { useCallback, useState } from 'react';
import type { FileRejection } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';

import useWizardContext from '@swf/core/hooks/useWizardContext';

import Upload from '../../../../../../assets/icons/dropzone-upload.svg';
import { Error } from '../../../../../../types/paymentVerification';
import ButtonGroup from '../../../../../ButtonGroup/ButtonGroup';
import CTAButton from '../../../../../CTAButton/CTAButton';
import PaymentVerificationPrompt from '../../../PaymentVerificationPrompt/PaymentVerificationPrompt';
import ConfirmationCheckbox from '../ConfirmationCheckbox/ConfirmationCheckbox';
import SubmitButton from '../SubmitButton/SubmitButton';
import UploadError from '../UploadError/UploadError';
import UploadSuccess from '../UploadSuccess/UploadSuccess';

import {
  TXTCLI_Confirm,
  TXTWCL_Drag_and_drop_a_file_here_or_c_ELL,
  TXTWCL_I_canXt_upload_my_file,
  TXTWCL_JPGX_PNGX_PDF_files_X10MBX
} from './DocumentsUpload.messages.json';
import {
  active,
  dropzone,
  dropzoneHidden,
  errorBigSpacing,
  errorSmallSpacing,
  invalid,
  titleWrapper
} from './DocumentsUpload.scss';

const DUPLICATE_FILE = 'duplicateFile';
const MAX_FILE_SIZE = 10000000;

interface Props {
  existingFiles: File[];
  onChange: (addedFiles: File[]) => void;
}

const DocumentsUpload: React.FC<Props> = ({ existingFiles, onChange }) => {
  const {
    data: { unverifiedOptions }
  } = useWizardContext();
  const [error, setError] = useState<Error | undefined>();
  const [showPaymentVerificationPrompt, setShowPaymentVerificationPrompt] =
    useState<boolean>(false);

  const MAX_FILES = Math.min(unverifiedOptions?.length * 5, 15) || 5;

  const onDelete = (file: File) => {
    const newFiles = existingFiles?.filter((f: File) => f !== file);
    onChange(newFiles);
    setError(undefined);
  };

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      const totalAcceptedFiles = existingFiles.length + acceptedFiles.length;
      const totalAddedFiles = totalAcceptedFiles + rejectedFiles.length;

      const hasRejectedFiles = !isEmpty(rejectedFiles);

      switch (true) {
        case totalAddedFiles > MAX_FILES: {
          setError(Error.MaxFiles);
          return;
        }

        case hasRejectedFiles && rejectedFiles.some(({ file }) => file.size > MAX_FILE_SIZE): {
          setError(Error.Size);
          break;
        }
        case hasRejectedFiles &&
          rejectedFiles.some(({ errors }) => errors[0].code === DUPLICATE_FILE): {
          setError(Error.Duplicate);
          break;
        }
        case hasRejectedFiles &&
          rejectedFiles.some(({ file }) => !/(jpg|jpeg|png|pdf)$/.test(file.type)): {
          setError(Error.Type);
          break;
        }
        case hasRejectedFiles: {
          setError(Error.Default);
          break;
        }

        default:
          setError(undefined);
          break;
      }

      if (!isEmpty(acceptedFiles)) {
        onChange([...existingFiles, ...acceptedFiles]);
      }
    },
    [existingFiles, onChange, MAX_FILES]
  );

  const validator = (file: File) => {
    if (existingFiles?.some((f: File) => f.name === file.name)) {
      return {
        code: DUPLICATE_FILE,
        message: DUPLICATE_FILE
      };
    }
    return null;
  };

  const { getInputProps, getRootProps, isDragActive, isDragReject } = useDropzone({
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpeg'],
      'image/jpg': ['.jpg'],
      'application/pdf': ['.pdf']
    },
    maxFiles: MAX_FILES,
    maxSize: MAX_FILE_SIZE,
    onDrop,
    validator
  });

  return (
    <>
      <section>
        <div
          {...getRootProps({
            className: classNames(
              isDragActive && active,
              isDragReject && invalid,
              existingFiles?.length < MAX_FILES ? dropzone : dropzoneHidden
            )
          })}
        >
          <input
            {...getInputProps()}
            id="deposit.imageField"
            name="deposit.imageField"
            data-testid="payment-images"
          />
          <div className={titleWrapper}>
            <Translation {...TXTWCL_Drag_and_drop_a_file_here_or_c_ELL} />
            <Translation tagName="p" {...TXTWCL_JPGX_PNGX_PDF_files_X10MBX} />
            <Upload />
          </div>
        </div>
        <aside>
          {!!existingFiles?.length &&
            existingFiles.map((file: File) => (
              <UploadSuccess key={file.name} acceptedFile={file} onDelete={() => onDelete(file)} />
            ))}
          {error && (
            <div className={existingFiles.length ? errorSmallSpacing : errorBigSpacing}>
              <UploadError error={error} />
            </div>
          )}
        </aside>
      </section>
      <ConfirmationCheckbox />
      <ButtonGroup>
        <SubmitButton
          translation={TXTCLI_Confirm}
          numberOfAddedFiles={existingFiles.length ?? 0}
          setError={setError}
        />
        <CTAButton
          type="button"
          variant="secondary"
          onClick={() => setShowPaymentVerificationPrompt(true)}
          translation={TXTWCL_I_canXt_upload_my_file}
        />
      </ButtonGroup>
      {showPaymentVerificationPrompt && (
        <PaymentVerificationPrompt onClose={() => setShowPaymentVerificationPrompt(false)} />
      )}
    </>
  );
};

DocumentsUpload.displayName = 'DocumentsUpload';

export default DocumentsUpload;
